// src/components/layout/WorkspaceSwitcher.tsx
// Workspace selector using bootContext ONLY in Central mode.
// In legacy mode, falls back to useGetWorkspacesQuery.

import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAppSelector } from '../../store/store';
import {
  selectBootCurrentWorkspace,
  selectBootOtherWorkspaces,
  selectIsBootReady,
  selectCreateWorkspaceUrl,
} from '../../store/slices/bootContextSlice';
import { useGetWorkspacesQuery, useSwitchWorkspaceMutation } from '../../api/workspacesApi';
import { Skeleton } from '../ui';

export default function WorkspaceSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { authSource, switchWorkspace: switchCentralWorkspace } = useAuth();

  const isCentralMode = authSource === 'central';

  // Central mode: use bootContext ONLY
  const bootCurrentWorkspace = useAppSelector(selectBootCurrentWorkspace);
  const bootOtherWorkspaces = useAppSelector(selectBootOtherWorkspaces);
  const isBootReady = useAppSelector(selectIsBootReady);
  const createWorkspaceUrl = useAppSelector(selectCreateWorkspaceUrl);

  // Legacy mode: use backend API
  const { data: legacyWorkspaces, isLoading: isLoadingLegacy } = useGetWorkspacesQuery(
    undefined,
    { skip: isCentralMode }
  );
  const [switchWorkspaceLegacy, { isLoading: isSwitchingLegacy }] = useSwitchWorkspaceMutation();

  const [isSwitchingCentral, setIsSwitchingCentral] = useState(false);
  const isSwitching = isCentralMode ? isSwitchingCentral : isSwitchingLegacy;
  const isLoading = isCentralMode ? !isBootReady : isLoadingLegacy;

  // Determine active workspace and dropdown items based on auth mode
  const activeWorkspaceName = isCentralMode
    ? bootCurrentWorkspace?.name
    : legacyWorkspaces?.find((ws) => ws.isActive)?.workspace.name;

  const activeWorkspaceId = isCentralMode
    ? bootCurrentWorkspace?.id
    : legacyWorkspaces?.find((ws) => ws.isActive)?.workspace.id;

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleSwitch = async (workspaceId: string) => {
    try {
      if (isCentralMode) {
        setIsSwitchingCentral(true);
        await switchCentralWorkspace(workspaceId);
        // BootGate detects new token and re-runs switch-context automatically
      } else {
        await switchWorkspaceLegacy(workspaceId).unwrap();
      }
      setIsOpen(false);
    } catch (error: any) {
      console.error('[WorkspaceSwitcher] Failed to switch workspace:', error);
      const errorMessage = error?.data?.error || error?.message || 'Failed to switch workspace';
      alert(`Workspace Switch Failed\n\n${errorMessage}\n\nPlease try again.`);
    } finally {
      setIsSwitchingCentral(false);
    }
  };

  if (isLoading) {
    return <Skeleton width={128} height={32} variant="rectangular" className="rounded-lg" />;
  }

  // Build dropdown items
  const dropdownItems = isCentralMode
    ? bootOtherWorkspaces.map((ws) => ({
        id: ws.id,
        name: ws.name,
        type: ws.type,
        role: ws.role,
        isSelected: false, // otherWorkspaces never includes current
      }))
    : (legacyWorkspaces || []).map((ws) => ({
        id: ws.workspace.id,
        name: ws.workspace.name,
        type: ws.workspace.type,
        role: ws.role,
        isSelected: ws.isActive,
      }));

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Current workspace: ${activeWorkspaceName || 'None selected'}`}
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors disabled:opacity-50"
      >
        <span className="truncate max-w-[120px]">
          {activeWorkspaceName || 'Select Workspace'}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label="Workspaces"
          className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
        >
          {/* Show current workspace as selected at top (Central mode) */}
          {isCentralMode && bootCurrentWorkspace && (
            <div
              role="option"
              aria-selected={true}
              className="w-full text-left px-4 py-2 bg-blue-50 flex items-center justify-between"
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {bootCurrentWorkspace.name}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {bootCurrentWorkspace.type} · {bootCurrentWorkspace.role}
                </p>
              </div>
              <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
            </div>
          )}

          {dropdownItems.map((item) => {
            const isSelected = isCentralMode ? false : item.isSelected;
            const isCurrentWorkspace = item.id === activeWorkspaceId;

            return (
              <button
                key={item.id}
                role="option"
                aria-selected={isSelected || isCurrentWorkspace}
                onClick={() => handleSwitch(item.id)}
                disabled={isCurrentWorkspace || isSwitching}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between transition-colors ${
                  isSelected || isCurrentWorkspace ? 'bg-blue-50' : ''
                }`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500 capitalize">
                    {item.type} · {item.role}
                  </p>
                </div>
                {(isSelected || isCurrentWorkspace) && (
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                )}
              </button>
            );
          })}

          {dropdownItems.length === 0 && !isCentralMode && (
            <p className="px-4 py-2 text-sm text-gray-500">No workspaces available</p>
          )}
          {dropdownItems.length === 0 && isCentralMode && (
            <p className="px-4 py-2 text-sm text-gray-500">No other workspaces</p>
          )}

          {/* Create Workspace link */}
          <div className="border-t border-gray-200 mt-1 pt-1">
            <a
              href={createWorkspaceUrl || '/workspaces'}
              className="block px-4 py-2 text-sm text-blue-600 hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              + Create New Workspace
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
